/**
 * Node Execution Detail - per-node prompt/input/result inspector for the Graph tab.
 * Statuses come from the already-polled (payload-less) `execution` prop; each instance's
 * prompts/inputs/result are fetched lazily, only once that instance is actually shown.
 */

import React, { useEffect, useState } from 'react';
import pipelineApi from '../api/client';
import type { PipelineExecution, StepInstanceExecution } from '../types/pipeline';
import { copyToClipboard } from '../utils/clipboard';
import { JsonViewer } from './JsonViewer';
import { CloseIcon, CopyIcon } from './icons';
import './NodeExecutionDetail.css';

interface NodeExecutionDetailProps {
  // Key into execution.node_executions - the backend step_name, not necessarily the
  // raw pipeline node id (callers reconcile that, e.g. via sanitizeStepName).
  nodeId: string;
  nodeLabel?: string;
  execution: PipelineExecution;
  onClose?: () => void;
}

// A step that hasn't produced any renderable detail yet reads very differently
// depending on where it is in its lifecycle: still pending/running is expected
// and not worth alarming over, while a completed step with nothing to show means
// this step type simply doesn't persist a retrievable result (see Part C's named
// gap for embedding/decomposition/extraction-style tasks).
const emptyStateMessage = (status: StepInstanceExecution['status']): string =>
  status === 'pending' || status === 'running'
    ? 'Not yet executed.'
    : "This step type doesn't persist a retrievable result.";

// Fetches one instance's full payload while `enabled`, refetching whenever its polled
// status changes (e.g. running -> completed brings in the result). Until the fetch lands
// - or for instances with no task_id to fetch by - the polled instance is returned as-is.
export function useStepInstanceDetail(
  executionId: string,
  instance: StepInstanceExecution | undefined,
  enabled = true,
): { detail: StepInstanceExecution | undefined; loading: boolean; error: string | null } {
  const [detail, setDetail] = useState<StepInstanceExecution | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const taskId = instance?.task_id;
  const status = instance?.status;

  useEffect(() => {
    setDetail(null);
    setError(null);
    if (!enabled || !taskId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    pipelineApi
      .getStepInstanceDetail(executionId, taskId)
      .then((fetched) => {
        if (!cancelled) setDetail(fetched);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load step detail');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [executionId, taskId, status, enabled]);

  return { detail: detail ?? instance, loading, error };
}

const LazyInstanceBody: React.FC<{ executionId: string; instance: StepInstanceExecution }> = ({
  executionId,
  instance,
}) => {
  const { detail, loading, error } = useStepInstanceDetail(executionId, instance);

  if (loading) {
    return <p className="node-detail-empty-note">Loading details...</p>;
  }

  return (
    <>
      {error && <p className="node-detail-empty-note">Failed to load details: {error}</p>}
      <InstanceBody instance={detail ?? instance} />
    </>
  );
};

const InstanceBody: React.FC<{ instance: StepInstanceExecution }> = ({ instance }) => {
  const hasDetail =
    instance.rendered_system_prompt ||
    instance.rendered_user_prompt ||
    instance.inputs ||
    instance.result ||
    instance.error;

  return (
    <div className="node-detail-instance-body">
      {instance.task_id && (
        <div className="node-execution-meta">
          <div className="meta-entry">
            <span className="meta-label">Task ID:</span>
            <code className="meta-value-full">{instance.task_id}</code>
            <button
              className="copy-button-small"
              onClick={() => copyToClipboard(instance.task_id!)}
              title="Copy task ID to clipboard"
            >
              <CopyIcon />
            </button>
          </div>
        </div>
      )}

      {instance.error && (
        <div className="node-execution-error">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" fill="none" />
            <path d="M8 4v5M8 11h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <div>
            <strong>Error:</strong>
            <pre>{instance.error}</pre>
          </div>
        </div>
      )}

      {instance.rendered_system_prompt && (
        <details className="data-section" open>
          <summary className="data-section-header">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M4 6l4 4 4-4H4z" />
            </svg>
            <span>System Prompt</span>
          </summary>
          <div className="data-content">
            <pre className="json-viewer">{instance.rendered_system_prompt}</pre>
          </div>
        </details>
      )}

      {instance.rendered_user_prompt && (
        <details className="data-section" open>
          <summary className="data-section-header">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M4 6l4 4 4-4H4z" />
            </svg>
            <span>User Prompt</span>
          </summary>
          <div className="data-content">
            <pre className="json-viewer">{instance.rendered_user_prompt}</pre>
          </div>
        </details>
      )}

      {instance.inputs && (
        <details className="data-section">
          <summary className="data-section-header">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M4 6l4 4 4-4H4z" />
            </svg>
            <span>Inputs</span>
          </summary>
          <div className="data-content">
            <JsonViewer data={instance.inputs} />
          </div>
        </details>
      )}

      {instance.result && (
        <details className="data-section" open>
          <summary className="data-section-header">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M4 6l4 4 4-4H4z" />
            </svg>
            <span>Result</span>
          </summary>
          <div className="data-content">
            <JsonViewer data={instance.result} />
          </div>
        </details>
      )}

      {!hasDetail && (
        <p className="node-detail-empty-note">{emptyStateMessage(instance.status)}</p>
      )}
    </div>
  );
};

export const NodeExecutionDetail: React.FC<NodeExecutionDetailProps> = ({ nodeId, nodeLabel, execution, onClose }) => {
  const nodeExec = execution.node_executions[nodeId];
  // Only expanded instances fetch their payload - a fan-out step can have hundreds.
  const [openItemKeys, setOpenItemKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    setOpenItemKeys(new Set());
  }, [nodeId]);

  const toggleItemKey = (itemKey: string, open: boolean) => {
    setOpenItemKeys((prev) => {
      const next = new Set(prev);
      if (open) next.add(itemKey);
      else next.delete(itemKey);
      return next;
    });
  };

  return (
    <div className="node-execution-detail">
      <div className="node-execution-detail-header">
        <h4>{nodeLabel || nodeId}</h4>
        {onClose && (
          <button className="button-secondary" onClick={onClose} title="Close panel">
            <CloseIcon />
          </button>
        )}
      </div>

      {!nodeExec ? (
        <p className="node-detail-empty-note">No execution data yet for this node.</p>
      ) : nodeExec.instances.length === 0 ? (
        <p className="node-detail-empty-note">{emptyStateMessage(nodeExec.status)}</p>
      ) : nodeExec.instances.length === 1 ? (
        <LazyInstanceBody executionId={execution.id} instance={nodeExec.instances[0]} />
      ) : (
        <div className="node-detail-instances">
          {nodeExec.instances.map(instance => (
            <details
              key={instance.item_key}
              className="node-detail-instance-collapsible"
              onToggle={(e) => toggleItemKey(instance.item_key, e.currentTarget.open)}
            >
              <summary>
                <code className="node-detail-item-key">{instance.item_key}</code>
                <span className={`status-badge status-badge-${instance.status}`}>{instance.status.toLowerCase()}</span>
              </summary>
              {openItemKeys.has(instance.item_key) && (
                <LazyInstanceBody executionId={execution.id} instance={instance} />
              )}
            </details>
          ))}
        </div>
      )}
    </div>
  );
};
